import type { ComponentProps, PropsWithChildren, ReactNode } from "react";
import { useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Badge, Dropdown } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { CrmManagementDisabledTooltip } from "app/pages/sales-intelligence/common-components/CrmManagementDisabledTooltip";
import { useCrmManagementReadOnly } from "app/pages/sales-intelligence/hooks/useCrmManagementReadOnly";
import {
    StyledArrow,
    StyledDescription,
    StyledExcludeSelectWithLabel,
    StyledIntegrationInfo,
    StyledIntegrationTabContainer,
    StyledSelectedFields,
    StyledSettingHeader,
} from "app/pages/sales-intelligence/pages/manage-integration/styles/styles";
import { WrappedWithPreloader } from "app/pages/sales-intelligence/pages/management/components/common/WrappedWithPreloader/WrappedWithPreloader";
import { StyledDropdownItem } from "app/pages/workspace/sales/components/SingleSelectDropdown/styles";

import InfoIconTooltip from "../../../../common-components/tooltip/InfoIconTooltip/InfoIconTooltip";
import { StyledOnOffSwitch } from "../AccountTab/style";

import { StyledDropdownContainer, StyledIntegrationSetting } from "./styles";

export type DropdownProps<T extends string> = {
    description?: string;
    tooltipContentInfo?: string;
    options: {
        value: T;
        label: string;
    }[];
    value: T;
    onSelect(value: T): void;
    disabled?: boolean;
    isLoading?: boolean;
};

export type IntegrationSettingProps<T extends string = string> = {
    title: string;
    content: string;
    onToggle(isEnabled: boolean): void;
    isEnabled: boolean;
    dataAutomation?: string;
    badgeType?: ComponentProps<typeof Badge>["type"];
    badge?: ReactNode;
    isDisabled?: boolean;
    dropdownProps?: DropdownProps<T>;
};

export const IntegrationSetting = <T extends string = string>({
    onToggle,
    content,
    title,
    isEnabled,
    isDisabled,
    dataAutomation,
    dropdownProps,
    badgeType,
    badge,
    children,
}: PropsWithChildren<IntegrationSettingProps<T>>) => {
    const { tooltipContentInfo, options, value, onSelect } = dropdownProps || {};
    const { isReadOnly } = useCrmManagementReadOnly();

    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = ({ id }: { id: T }) => onSelect?.(id);

    const renderDropdownButton = () => {
        const currentField = options?.find((option) => {
            return option.value === value;
        });

        const buttonLabel = currentField?.label || value;

        return (
            <WrappedWithPreloader
                isLoading={!!dropdownProps?.isLoading}
                width={100}
                height={15}
                key={`${value}-button`}
            >
                <StyledSelectedFields
                    isDisabled={!isEnabled || isReadOnly}
                    isSelected={!isOpen}
                    id={value}
                >
                    {buttonLabel}
                    <StyledArrow>&#9662;</StyledArrow>
                </StyledSelectedFields>
            </WrappedWithPreloader>
        );
    };

    const renderDropdownItems = () => {
        return (
            options?.map((option) => {
                return (
                    <StyledDropdownItem
                        selected={option.value === value}
                        disabled={option.value === value}
                        id={option.value}
                        key={option.value}
                    >
                        {option.label}
                    </StyledDropdownItem>
                );
            }) ?? []
        );
    };

    const renderDropdownChildren = () => {
        return [renderDropdownButton()].concat(renderDropdownItems());
    };

    const handleDropdownToggle = (isOpen: boolean) => setIsOpen(isOpen);

    const renderToggle = (readOnly: boolean) => (
        <StyledOnOffSwitch
            isSelected={isEnabled}
            onClick={() => onToggle(!isEnabled)}
            isDisabled={isDisabled || readOnly}
        />
    );

    const renderDropdown = () => {
        return (
            <StyledExcludeSelectWithLabel className="Dropdown-container">
                <Dropdown
                    onToggle={handleDropdownToggle}
                    width={320}
                    selectedIds={{ [value!]: true }}
                    onClick={handleSelect}
                    disabled={!isEnabled || dropdownProps?.disabled || isReadOnly}
                    dropdownPopupPlacement="bottom-left"
                    appendTo=".Dropdown-container"
                >
                    {renderDropdownChildren()}
                </Dropdown>
                &nbsp;&nbsp;
                {tooltipContentInfo ? <InfoIconTooltip tooltipContent={tooltipContentInfo} /> : null}
            </StyledExcludeSelectWithLabel>
        );
    };

    return (
        <StyledIntegrationSetting data-automation={dataAutomation}>
            <StyledIntegrationTabContainer>
                <StyledSettingHeader>
                    <Text
                        fontVariant="subtitle1"
                        color={colorsPalettes.carbon[400]}
                    >
                        {title}
                    </Text>
                    {badge ?? (badgeType ? <Badge type={badgeType} /> : null)}
                </StyledSettingHeader>
                <Flex
                    flexDirection="column"
                    gap="28px"
                >
                    <Flex alignItems="flex-start">
                        <CrmManagementDisabledTooltip renderContent={renderToggle} />
                        <StyledDescription>
                            <StyledIntegrationInfo>{content}&nbsp;</StyledIntegrationInfo>
                            {dropdownProps?.description ? (
                                <StyledDropdownContainer>
                                    <StyledIntegrationInfo>{dropdownProps.description}&nbsp;</StyledIntegrationInfo>
                                    {renderDropdown()}
                                </StyledDropdownContainer>
                            ) : (
                                dropdownProps && renderDropdown()
                            )}
                        </StyledDescription>
                    </Flex>

                    {children}
                </Flex>
            </StyledIntegrationTabContainer>
        </StyledIntegrationSetting>
    );
};
